import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { X, Zap, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { documentStoreService } from '@/services/document-store.service';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';

interface DocumentStoreFormValues {
  name: string;
  description: string;
}

interface UpdateDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  document: { id: string; name: string; description?: string } | null;
}

export default function UpdateDocumentModal({ isOpen, onClose, onSubmit, document }: UpdateDocumentModalProps) {
  const [isEnhancing, setIsEnhancing] = useState(false);

  const {
    handleSubmit,
    formState: { isSubmitting },
    reset,
    setValue,
    watch,
    control,
  } = useForm<DocumentStoreFormValues>({
    // No resolver: the shared Input/Textarea apply their clean-text rule on top of the field rules
    // Validate while typing so the clean-text error shows under the field, not only after Save
    mode: 'onChange',
    defaultValues: { name: '', description: '' },
  });

  useEffect(() => {
    if (document) {
      setValue('name', document.name);
      setValue('description', document.description || '');
    } else {
      reset();
    }
  }, [document, setValue, reset]);

  const handleClose = () => {
    reset();
    setIsEnhancing(false);
    onClose();
  };

  const handleMagicClick = async () => {
    const currentDescription = watch('description');
    const storeName = watch('name');

    if (!currentDescription || currentDescription.trim().length === 0) {
      toast.error('Please enter a description first');
      return;
    }

    if (!storeName || storeName.trim().length === 0) {
      toast.error('Please enter a knowledge store name first');
      return;
    }

    setIsEnhancing(true);

    try {
      const result = await documentStoreService.enhanceDescription(
        storeName,
        currentDescription
      );

      if (result.enhanced_description) {
        setValue('description', result.enhanced_description, { shouldValidate: true });
        toast.success('Description enhanced successfully!');

        // Optionally show suggestions
        if (result.suggestions && result.suggestions.length > 0) {
          console.log('💡 Organizational Suggestions:', result.suggestions);
        }

        // Optionally show key topics
        if (result.key_topics && result.key_topics.length > 0) {
          console.log('🏷️ Key Topics:', result.key_topics);
        }
      }
    } catch (error: any) {
      console.error('Error enhancing description:', error);
      toast.error(error.response?.data?.description || error.message || 'Failed to enhance description. Please try again.');
    } finally {
      setIsEnhancing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 common-modal-overlay">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Update Knowledge Store</h2>
            <button onClick={handleClose} className="p-1 rounded-full hover:bg-gray-100">
              <X size={24} />
            </button>
          </div>
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-6 space-y-4">
            <Input<DocumentStoreFormValues>
              name="name"
              control={control}
              label="Knowledge Store Name"
              type="text"
              rules={{ required: 'Knowledge store name is required' }}
            />
            <Textarea<DocumentStoreFormValues>
              name="description"
              control={control}
              label="Description"
              rows={4}
              rules={{ required: 'Description is required' }}
              className="pr-10"
              overlay={
                <button
                  type="button"
                  onClick={handleMagicClick}
                  disabled={isEnhancing}
                  className="absolute top-2 right-2 p-1 text-gray-500 hover:text-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={isEnhancing ? "Enhancing..." : "Improve with AI"}
                >
                  {isEnhancing ? (
                    <Loader2 size={20} className="animate-spin" />
                  ) : (
                    <Zap size={20} />
                  )}
                </button>
              }
            />
          </div>
          <div className="p-6 bg-gray-50 rounded-b-lg flex justify-end gap-4">
            <button type="button" onClick={handleClose} className="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Updating...' : 'Update'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
